import React, { useState, useEffect } from "react";
import Modal from "./Modal";
import countryCodes from "../data/countryCodes.json";

interface FormData {
  email: string;
  password: string;
  family_name: string;
  given_name: string;
  phone_number: string;
  street_address: string;
  city: string;
  state: string;
  country: string;
  postal_code: string;
  company_name: string;
  company_title: string;
  company_phone_number: string;
  company_street_address: string;
  company_city: string;
  company_state: string;
  company_country: string;
  company_postal_code: string;
  terms_agreed: boolean;
}


interface SignUpFormProps {
  onSubmit: (formData: FormData) => void;
  initialEmail?: string;
}

export const SignUpForm = ({ onSubmit, initialEmail }: SignUpFormProps) => {
  const [formData, setFormData] = useState<FormData>({
    email: initialEmail || "",
    password: "",
    family_name: "",
    given_name: "",
    phone_number: "",
    street_address: "",
    city: "",
    state: "",
    country: "",
    postal_code: "",
    company_name: "",
    company_title: "",
    company_phone_number: "",
    company_street_address: "",
    company_city: "",
    company_state: "",
    company_country: "",
    company_postal_code: "",
    terms_agreed: false,
  });


  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [termsContent, setTermsContent] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get sorted country names from the JSON file
  const countryNames = Object.keys(countryCodes).sort();

  useEffect(() => {
    fetch("/Terms.html")
      .then((response) => response.text())
      .then((data) => setTermsContent(data))
      .catch((error) => console.error("Error fetching terms:", error));

    window.closeModal = () => setIsModalOpen(false);
  }, []);

  const formatPhoneNumber = (value: string) => {
    // Remove all non-digit characters
    const numbers = value.replace(/\D/g, "");

    // Handle country code - if it starts with 1 and has 11 digits, remove the leading 1
    let cleanNumbers = numbers;
    if (numbers.length === 11 && numbers.startsWith('1')) {
      cleanNumbers = numbers.substring(1);
    }

    // Format the 10-digit number
    if (cleanNumbers.length <= 3) {
      return cleanNumbers;
    } else if (cleanNumbers.length <= 6) {
      return `${cleanNumbers.slice(0, 3)}-${cleanNumbers.slice(3)}`;
    } else {
      return `${cleanNumbers.slice(0, 3)}-${cleanNumbers.slice(3, 6)}-${cleanNumbers.slice(6, 10)}`;
    }
  };


  const formatPostalCode = (value: string, country: string) => {
    // Remove all spaces and convert to uppercase
    const cleaned = value.replace(/\s/g, '').toUpperCase();

    // Canadian postal code format: A1A 1A1
    if (country === 'Canada') {
      // Remove non-alphanumeric characters
      const alphanumeric = cleaned.replace(/[^A-Z0-9]/g, '');

      if (alphanumeric.length <= 3) {
        return alphanumeric;
      } else if (alphanumeric.length <= 6) {
        return `${alphanumeric.slice(0, 3)} ${alphanumeric.slice(3)}`;
      } else {
        return `${alphanumeric.slice(0, 3)} ${alphanumeric.slice(3, 6)}`;
      }
    }

    // For other countries, just return cleaned value (no formatting)
    return cleaned;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePhoneChange = (fieldName: string, value: string) => {
    const formattedValue = formatPhoneNumber(value);
    setFormData((prev) => ({
      ...prev,
      [fieldName]: formattedValue,
    }));
  };

  const handlePostalCodeChange = (fieldName: string, value: string) => {
    // Determine which country to use for formatting
    const country = fieldName === 'postal_code' ? formData.country : formData.company_country;
    const formattedValue = formatPostalCode(value, country);
    setFormData((prev) => ({
      ...prev,
      [fieldName]: formattedValue,
    }));
  };

  const handleTermsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;
    setAcceptedTerms(checked);
    setFormData((prev) => ({
      ...prev,
      terms_agreed: checked,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!acceptedTerms) {
      setError("You must accept the terms and services to continue.");
      return;
    }
    setError(null);
    setIsSubmitting(true);
    try {
      await onSubmit(formData);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="signup-form-container">
      <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
        <h2 className="text-center text-2xl font-bold text-white mb-6">
          Create your account
        </h2>

        <div className="space-y-6">
          {/* Personal Information Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white border-b border-gray-600 pb-2">
              Personal Information
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  First Name
                </label>
                <input
                  type="text"
                  name="given_name"
                  value={formData.given_name}
                  onChange={handleChange}
                  placeholder="Enter your first name"
                  autoComplete="given-name"
                  required
                  className="block w-full border border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-gray-700 text-white text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Last Name
                </label>
                <input
                  type="text"
                  name="family_name"
                  value={formData.family_name}
                  onChange={handleChange}
                  placeholder="Enter your last name"
                  autoComplete="family-name"
                  required
                  className="block w-full border border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-gray-700 text-white text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter your email"
                autoComplete="email"
                required
                className="block w-full border border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-gray-700 text-white text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Password
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter your password"
                autoComplete="new-password"
                required
                className="block w-full border border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-gray-700 text-white text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Street Address
              </label>
              <input
                type="text"
                name="street_address"
                value={formData.street_address}
                onChange={handleChange}
                placeholder="Enter your street address"
                autoComplete="street-address"
                required
                className="block w-full border border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-gray-700 text-white text-sm"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  City
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="Enter your city"
                  autoComplete="address-level2"
                  required
                  className="block w-full border border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-gray-700 text-white text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  State / Province
                </label>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  placeholder="Enter state or province"
                  autoComplete="address-level1"
                  maxLength={2}
                  className="block w-full border border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-gray-700 text-white text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Postal Code / Zip Code
                </label>
                <input
                  type="text"
                  name="postal_code"
                  value={formData.postal_code}
                  onChange={(e) => handlePostalCodeChange('postal_code', e.target.value)}
                  placeholder="Enter your postal or zip code"
                  autoComplete="postal-code"
                  maxLength={7}
                  required
                  className="block w-full border border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-gray-700 text-white text-sm"
                />
              </div>
            </div>


            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Country
              </label>
              <select
                name="country"
                value={formData.country}
                onChange={handleChange}
                autoComplete="country-name"
                required
                className="block w-full border border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-gray-700 text-white text-sm"
              >
                <option value="">Select your country</option>
                {countryNames.map((countryName) => (
                  <option key={countryName} value={countryName}>
                    {countryName}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Company Information Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white border-b border-gray-600 pb-2">
              Company Information
            </h3>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Company Name
              </label>
              <input
                type="text"
                name="company_name"
                value={formData.company_name}
                onChange={handleChange}
                placeholder="Enter your company name"
                autoComplete="organization"
                className="block w-full border border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-gray-700 text-white text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Your Title
              </label>
              <input
                type="text"
                name="company_title"
                value={formData.company_title}
                onChange={handleChange}
                placeholder="Enter your job title"
                autoComplete="organization-title"
                className="block w-full border border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-gray-700 text-white text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Company Phone Number
              </label>
              <input
                type="tel"
                name="company_phone_number"
                value={formData.company_phone_number}
                onChange={(e) => handlePhoneChange('company_phone_number', e.target.value)}
                maxLength={12}
                placeholder="555-555-5555"
                pattern="[0-9]{3}-[0-9]{3}-[0-9]{4}"
                autoComplete="tel"
                className="block w-full border border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-gray-700 text-white text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Company Street Address
              </label>
              <input
                type="text"
                name="company_street_address"
                value={formData.company_street_address}
                onChange={handleChange}
                placeholder="Enter company street address"
                autoComplete="work street-address"
                className="block w-full border border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-gray-700 text-white text-sm"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Company City
                </label>
                <input
                  type="text"
                  name="company_city"
                  value={formData.company_city}
                  onChange={handleChange}
                  placeholder="Enter company city"
                  autoComplete="work address-level2"
                  className="block w-full border border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-gray-700 text-white text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  State / Province
                </label>
                <input
                  type="text"
                  name="company_state"
                  value={formData.company_state}
                  onChange={handleChange}
                  placeholder="Enter state or province"
                  autoComplete="work address-level1"
                  maxLength={2}
                  className="block w-full border border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-gray-700 text-white text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Postal Code / Zip Code
                </label>
                <input
                  type="text"
                  name="company_postal_code"
                  value={formData.company_postal_code}
                  onChange={(e) => handlePostalCodeChange('company_postal_code', e.target.value)}
                  placeholder="Enter company postal or zip code"
                  autoComplete="work postal-code"
                  maxLength={7}
                  className="block w-full border border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-gray-700 text-white text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Company Country
              </label>
              <select
                name="company_country"
                value={formData.company_country}
                onChange={handleChange}
                autoComplete="work country-name"
                className="block w-full border border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-gray-700 text-white text-sm"
              >
                <option value="">Select company country</option>
                {countryNames.map((countryName) => (
                  <option key={countryName} value={countryName}>
                    {countryName}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="flex items-center">
          <input
            id="terms"
            name="terms"
            type="checkbox"
            checked={acceptedTerms}
            onChange={handleTermsChange}
            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-600 rounded bg-gray-700"
          />
          <label htmlFor="terms" className="ml-2 block text-sm text-gray-300">
            I accept the{" "}
            <button
              type="button"
              onClick={() => setIsModalOpen(true)}
              className="text-indigo-400 hover:text-indigo-300 underline"
            >
              Terms and Conditions
            </button>
          </label>
        </div>

        {error && <span className="text-red-400 text-sm">{error}</span>}

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
        >
          {isSubmitting ? (
            <>
              <svg
                className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Creating Account...
            </>
          ) : (
            "Create Account"
          )}
        </button>
      </form>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <div dangerouslySetInnerHTML={{ __html: termsContent }} />
      </Modal>
    </div>
  );
};

